/**
 * Reveal — the kit's presentation-animation primitive.
 *
 * Wrap content to ease it in. On mount (the default) it runs a pure-CSS
 * keyframe from styles/motion.css, so it works server-rendered with no JS and
 * never leaves content stuck hidden. `whenVisible` defers the reveal until the
 * element scrolls into view (IntersectionObserver) — for below-the-fold content.
 *
 * Stagger a list by passing an increasing `delay`:
 *   {items.map((it, i) => <Reveal key={it.id} delay={i * 60}>…</Reveal>)}
 *
 * Animates only opacity/transform (CLS-safe), and honors the OS reduced-motion
 * setting automatically via the global guard in styles/motion.css.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
  type MutableRefObject,
  type ReactElement,
} from 'react';
import styles from './Reveal.module.css';

export type RevealVariant = 'fade' | 'up' | 'down' | 'scale';

export interface RevealProps extends HTMLAttributes<HTMLDivElement> {
  /** Entrance style. Default `up` (fade + 8px rise). */
  readonly variant?: RevealVariant;
  /** Delay before the entrance, in ms. Stagger a list with `delay={i * 60}`. */
  readonly delay?: number;
  /** Duration override, in ms. Defaults to the `--dur-base` motion token. */
  readonly duration?: number;
  /**
   * Reveal when the element scrolls into view instead of on mount — for
   * below-the-fold content. Default false (reveal on mount: pure-CSS, SSR-safe).
   */
  readonly whenVisible?: boolean;
  /** With `whenVisible`, reveal once then stop observing (default true). */
  readonly once?: boolean;
}

const VARIANT_CLASS: Record<RevealVariant, string> = {
  fade: styles.fade ?? '',
  up: styles.up ?? '',
  down: styles.down ?? '',
  scale: styles.scale ?? '',
};

interface UseInViewResult {
  /** Callback ref — attach to the element you want to observe. */
  readonly ref: (node: Element | null) => void;
  /** True once the element has intersected the viewport. */
  readonly inView: boolean;
}

/**
 * Fire when an element scrolls into view. Returns a stable callback `ref` to
 * attach and the current `inView` flag. Falls back to `inView: true` where
 * IntersectionObserver is unavailable (older runtimes / SSR hand-off).
 */
export function useInView(options?: {
  readonly once?: boolean;
  readonly threshold?: number;
}): UseInViewResult {
  const once = options?.once ?? true;
  const threshold = options?.threshold ?? 0.12;
  const [inView, setInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const ref = useCallback(
    (node: Element | null): void => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;
      if (typeof IntersectionObserver === 'undefined') {
        setInView(true);
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting) {
            setInView(true);
            if (once) observer.disconnect();
          } else if (!once) {
            setInView(false);
          }
        },
        { threshold },
      );
      observer.observe(node);
      observerRef.current = observer;
    },
    [once, threshold],
  );

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { ref, inView };
}

export const Reveal = forwardRef<HTMLDivElement, RevealProps>(function Reveal(
  {
    variant = 'up',
    delay = 0,
    duration,
    whenVisible = false,
    once = true,
    className,
    style,
    children,
    ...rest
  },
  ref,
): ReactElement {
  const { ref: inViewRef, inView } = useInView({ once });
  const shown = whenVisible ? inView : true;

  const setRef = useCallback(
    (node: HTMLDivElement | null): void => {
      if (whenVisible) inViewRef(node);
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as MutableRefObject<HTMLDivElement | null>).current = node;
    },
    [whenVisible, inViewRef, ref],
  );

  const mergedStyle = {
    ...style,
    '--reveal-delay': `${delay}ms`,
    ...(duration !== undefined ? { '--reveal-duration': `${duration}ms` } : {}),
  } as CSSProperties;

  const cls = shown
    ? [styles.reveal ?? '', VARIANT_CLASS[variant], className].filter(Boolean).join(' ')
    : [styles.pending ?? '', className].filter(Boolean).join(' ');

  return (
    <div ref={setRef} className={cls} style={mergedStyle} {...rest}>
      {children}
    </div>
  );
});
